'use client';

import { Placeholder, Text } from '@lmn/design-system';
import styled from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { isSplitPaymentsEligible } from '@/app/components/search/components/DeferredPayments/splitPayments.ts';
import { Icon } from '@/app/common/icons/Icon';
import customCss from '../css/customCss';

export function MealPlanDetails(
  props: {
    mealPlan: MealPlanType;
    hideAtol?: boolean;
    hideDeferredPayments?: boolean;
  } & React.HTMLAttributes<HTMLDivElement>
) {
  const { mealPlan, hideAtol = false, hideDeferredPayments = false, ...rest } = props;
  const { t } = useTranslation();
  const initialParams = useInitialParams();
  const urlSearchParams = useUrlSearchParams();
  const { localization } = usePageWrapperData();

  const showDeferredPayments = !hideDeferredPayments && !!mealPlan?.deferredPaymentDeposit;
  const showSplitPayments =
    !hideDeferredPayments &&
    isSplitPaymentsEligible({
      localization,
      totalPrice: mealPlan?.finalTotalPrice,
      isHO: urlSearchParams.accommodationOnly,
    });
  const showPaymentInfo = showDeferredPayments || showSplitPayments;
  const showAtol = !hideAtol && initialParams.atol && !urlSearchParams.accommodationOnly;

  return mealPlan?.backToTravel || showPaymentInfo || showAtol ? (
    <DetailsInfo {...rest}>
      {mealPlan?.backToTravel ? (
        <DetailInfoRow>
          <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt="Travel Extra Pack included" />
          <Text variant="BodySmall">{t('vacation.select.summary.tep_included')}</Text>
        </DetailInfoRow>
      ) : undefined}
      {showPaymentInfo ? (
        <DetailInfoRow>
          <MealPlanDetailsDeferredPayments mealPlan={mealPlan} />
        </DetailInfoRow>
      ) : undefined}
      {showAtol ? (
        <DetailInfoRow>
          <Icon
            category="Semantics"
            name="icon-check"
            size={16}
            color="iconPrimary"
            alt={urlSearchParams.isTrain ? 'Abtot Protected included' : 'Atol Protected included'}
          />
          <Text variant="BodySmall">
            {t(urlSearchParams.isTrain ? 'vacation.select.abtot.protected' : 'vacation.select.atol.protected')}
          </Text>
        </DetailInfoRow>
      ) : undefined}
    </DetailsInfo>
  ) : undefined;
}

export function MealPlanDetailsDeferredPayments(props: { mealPlan: MealPlanType; iconSize?: number }) {
  const { mealPlan, iconSize = 16 } = props;
  const { localization } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { t } = useTranslation();
  const showDeferredPayments = !!mealPlan?.deferredPaymentDeposit;
  const showSplitPayments = isSplitPaymentsEligible({
    localization,
    totalPrice: mealPlan.finalTotalPrice,
    isHO: urlSearchParams.accommodationOnly,
  });
  const showOneEuroDeferredPayment =
    showDeferredPayments && mealPlan?.deferredPaymentDeposit === 1 && ['GBP', 'EUR'].includes(localization.currency);

  const getLabel = () => {
    if (showSplitPayments) {
      return urlSearchParams.accommodationOnly
        ? t('vacation.select.details.split_payments_HO.room.option')
        : t('vacation.select.details.split_payments.room.option');
    }

    if (showOneEuroDeferredPayment) {
      return t('vacation.select.details.deferredpayments_deposit.min_deposit.room.option', {
        amount: formatPrice({ price: mealPlan.deferredPaymentDeposit, localization }),
      });
    }

    return urlSearchParams.accommodationOnly
      ? t('vacation.select.details.deferredpayments_deposit_HO.room.option', {
          amount: formatPrice({ price: mealPlan.deferredPaymentDeposit, localization }),
        })
      : t('vacation.select.details.deferredpayments_deposit.room.option', {
          amount: formatPrice({ price: mealPlan.deferredPaymentDeposit, localization }),
        });
  };

  return showDeferredPayments || showSplitPayments ? (
    <>
      <Icon category="Semantics" name="icon-check" size={iconSize} color="iconPrimary" alt="Deposit available" />
      <Text variant="BodySmall">{getLabel()}</Text>
    </>
  ) : undefined;
}

export const DetailInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 2px 0;
`;

export const DetailsInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  row-gap: 2px;

  @media ${customCss.breakpoints.sm} {
    flex-grow: 0;
    flex-direction: row;
    flex-wrap: wrap;
    height: fit-content;
    column-gap: 16px;
  }
`;

export function MealPlanDetailsLoading() {
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 8px;
        padding: 4px 0;

        @media ${customCss.breakpoints.sm} {
          flex-direction: row;
          column-gap: 16px;
          flex-wrap: wrap;
        }
      `}
    >
      <Placeholder height="18px" width="200px" />
      <Placeholder height="18px" width="200px" />
      <Placeholder height="18px" width="120px" />
    </div>
  );
}
